import React, { useState, useEffect, useRef } from "react";
import "./style.css";
import Swal from "sweetalert2";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  base_url,
  deleteUserEvidence,
  disconnectStrava,
  getStravaActivity,
  getUserEvidences,
  submitEvidence,
} from "../../globalService/api.service";
import starvaLogo from "../../assets/starva-logo.png";
import footballImage from "../../assets/football.png";
import OcrImageUploader from "../../components/OcrImageUploader";

function formatDuration(sec: number | null | undefined) {
  const total = Number(sec) || 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

type ActivityItem = {
  id: string | number;
  name: string;
  distance: number;
  duration?: number;
  elapsed_time?: number;
  duration_hms?: string;
  isSelected?: boolean;
  isSubmitted?: boolean;
};

const Modal = ({ isOpen, onClose, getUser, status, app }) => {
  const [disEvedenceKm, setDisEvedenceKm] = useState<number | null>(null);
  const [durEvidenceSec, setDurEvidenceSec] = useState<number | null>(null);
  const [timeMinutes, setTimeMinutes] = useState<number>(0);
  const [timeRatio, setTimeRatio] = useState<number>(7);
  const [footballConversion, setFootballConversion] = useState<number>(7);
  const [submissionType, setSubmissionType] = useState<"distance" | "time" | "ocr">("distance");
  const [activeOption, setActiveOption] = useState<number>(1);
  const [appActivityData, setAppActivityData] = useState<ActivityItem[]>([]);

  const kmToMilesConversion = 0.621371;
  const distanceInMiles =
    disEvedenceKm != null ? disEvedenceKm * kmToMilesConversion : 0;

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { brandId, productId, userId } = params;

  const footballProductIds = ["76070"];
  const isFootballProduct = footballProductIds.includes(productId || "");

  useEffect(() => {
    if (isOpen) {
      getStrava();
    }
  }, [isOpen]);

  useEffect(() => {
    if (status) setActiveOption(2);
  }, [status]);

  const getStrava = async () => {
    try {
      const [activityData, evidences] = await Promise.all([
        getStravaActivity(userId),
        getUserEvidences(productId, userId),
      ]);

      const submittedIds = new Set(
        (Array.isArray(evidences) ? evidences : [])
          .flatMap((e: any) =>
            Array.isArray(e?.stravaActivityIds)
              ? e.stravaActivityIds
              : e?.stravaActivityId
              ? [e.stravaActivityId]
              : []
          )
          .filter(Boolean)
      );

      setAppActivityData((prev) => {
        const prevSelected = new Set(
          prev.filter((item) => item.isSelected).map((item) => item.id)
        );

        return (activityData?.data || []).map((activity: any) => {
          const normalizedDuration = Number(
            activity.duration ?? activity.elapsed_time ?? 0
          );

          return {
            ...activity,
            duration: normalizedDuration,
            duration_hms:
              activity.duration_hms || formatDuration(normalizedDuration),
            isSelected: prevSelected.has(activity.id) && !submittedIds.has(activity.id),
            isSubmitted: submittedIds.has(activity.id),
          };
        });
      });
    } catch (error) {
      console.log("error", error);
      setAppActivityData([]);
    }
  };

  const handleOptionSelect = (key: number) => {
    setActiveOption(key);
  };

  const handleDistanceChange = (disEve: number) => {
    setDisEvedenceKm(disEve);
  };

  const handleDurationChange = (durEve: number) => {
    setDurEvidenceSec(durEve);
  };

  if (!isOpen) return null;

  const onSubmitEvidence = async () => {
    let confirmationMessage = "";
    let payload: any = {};

    if (isFootballProduct) {
      const computedDistance = timeMinutes / footballConversion;
      const computedDurationSec = timeMinutes * 60;
      confirmationMessage = `Do you want to submit ${timeMinutes} minutes? (Equivalent to ${computedDistance.toFixed(
        2
      )} KM)`;
      payload = {
        distanceKm: computedDistance,
        durationSec: computedDurationSec,
      };
    } else if (submissionType === "time") {
      const computedDistance = timeMinutes / timeRatio;
      const computedDurationSec = timeMinutes * 60;
      confirmationMessage = `Do you want to submit ${timeMinutes} minutes? (Equivalent to ${computedDistance.toFixed(
        2
      )} KM)`;
      payload = {
        distanceKm: computedDistance,
        durationSec: computedDurationSec,
      };
    } else {
      const selectedActivityIds = appActivityData
        .filter((a) => a.isSelected)
        .map((a) => a.id);

      confirmationMessage = `Please check your distance and confirm ${(disEvedenceKm || 0).toFixed(
        2
      )} KM / ${distanceInMiles.toFixed(2)} Miles?`;

      payload = {
        distanceKm: disEvedenceKm || 0,
        durationSec: durEvidenceSec || 0,
        stravaActivityIds: selectedActivityIds,
      };
    }

    Swal.fire({
      title: confirmationMessage,
      showCancelButton: true,
      confirmButtonText: "CONFIRM",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await submitEvidence(productId, userId, payload);

          if (response?.is_success) {
            getUser();

            Swal.fire(
              "Evidence Saved",
              isFootballProduct || submissionType === "time"
                ? `Time: ${timeMinutes} minutes | Equivalent Distance: ${(
                    timeMinutes /
                    (isFootballProduct ? footballConversion : timeRatio)
                  ).toFixed(2)} KM`
                : `Distance: ${(disEvedenceKm || 0).toFixed(2)} KM${
                    durEvidenceSec
                      ? ` | Duration: ${formatDuration(durEvidenceSec)}`
                      : ""
                  }`,
              "success"
            );

            clearSelection();
            setDisEvedenceKm(null);
            setDurEvidenceSec(null);
            setTimeMinutes(0);
            await getStrava();
            onClose();
          } else {
            Swal.fire("Something went wrong", "Try again later", "error");
          }
        } catch (error) {
          console.log("err", error);
          Swal.fire("Something went wrong", "Try again later", "error");
        }
      }
    });
  };

  const tabs = [
    { id: 1, name: "ADD ACTIVITY", hideIfCompleted: true },
    { id: 2, name: "SUBMISSIONS" },
  ];

  const configureAndGetTabs = () => {
    if (status) {
      return tabs.filter((tab) => !tab.hideIfCompleted);
    }
    return tabs;
  };

  const evidenceFormConfig = [
    {
      key: "distance",
      component: (
        <DistanceConverter
          handleDistanceChange={handleDistanceChange}
          dis={disEvedenceKm}
        />
      ),
    },
    {
      key: "duration",
      component: (
        <DurationConverter
          handleDurationChange={handleDurationChange}
          dur={durEvidenceSec}
        />
      ),
    },
    {
      key: "image",
      component: <ImageUploader />,
    },
  ];

  const timeEvidenceFormConfig = [
    {
      key: "time",
      component: (
        <TimeConverter
          handleTimeChange={(min) => setTimeMinutes(min)}
          timeMinutes={timeMinutes}
        />
      ),
    },
    {
      key: "ratio",
      component: (
        <TimeRatioSelector
          ratio={timeRatio}
          handleRatioChange={(newRatio) => setTimeRatio(newRatio)}
        />
      ),
    },
    {
      key: "image",
      component: <ImageUploader />,
    },
  ];

  const footballEvidenceFormConfig = [
    {
      key: "time",
      component: (
        <TimeConverter
          handleTimeChange={(min) => setTimeMinutes(min)}
          timeMinutes={timeMinutes}
        />
      ),
    },
    {
      key: "footballSelector",
      component: (
        <FootballSelector
          handleFootballConversion={(newRatio) => setFootballConversion(newRatio)}
        />
      ),
    },
    {
      key: "image",
      component: <ImageUploader />,
    },
  ];

  const appsToConnect = [
    {
      name: "strava",
      logo: starvaLogo,
      redirectUrl: `${base_url}/auth/strava/connect/${brandId}/${productId}/${userId}`,
    },
  ];

  const handleClick = (redirectUrl?: string) => {
    if (!redirectUrl) return;
    window.location.href = redirectUrl;
  };

  const handleActivitySelect = (activity: ActivityItem) => {
    setAppActivityData((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== activity.id) return item;
        if (item.isSubmitted) return item;
        return { ...item, isSelected: !item.isSelected };
      });

      const total = updated.reduce(
        (acc, item) => {
          if (item.isSelected) {
            acc.distance += Number(item.distance) || 0;
            acc.duration += Number(item.duration ?? item.elapsed_time ?? 0) || 0;
          }
          return acc;
        },
        { distance: 0, duration: 0 }
      );

      setDisEvedenceKm(total.distance > 0 ? Number(total.distance.toFixed(2)) : null);
      setDurEvidenceSec(total.duration > 0 ? total.duration : null);

      return updated;
    });
  };

  const clearSelection = () => {
    setAppActivityData((prev) => prev.map((e) => ({ ...e, isSelected: false })));
  };

  const handleLogout = async () => {
    try {
      await disconnectStrava(userId);
    } catch (e) {
      console.log(e);
    }

    const { pathname, search } = location;
    const newSearch = search.replace(/(\?|&)app=strava/, "");
    navigate(`${pathname}${newSearch}`);
    setAppActivityData([]);
    setDisEvedenceKm(null);
    setDurEvidenceSec(null);
  };

  const closeAndReset = () => {
    setDisEvedenceKm(null);
    setDurEvidenceSec(null);
    setTimeMinutes(0);
    clearSelection();
    onClose();
  };

  const isSubmitDisabled = isFootballProduct
    ? !(timeMinutes && footballConversion)
    : submissionType === "time"
    ? !timeMinutes
    : submissionType === "ocr"
    ? false
    : !disEvedenceKm;

  return (
    <div className="modal evidence-modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-content evidence-modal-content">
        <div className="evidence-modal-header">
          <div className="evidence-modal-title">
            {activeOption === 2 ? "SUBMISSIONS" : "ADD ACTIVITY"}
          </div>
          <button
            type="button"
            className="evidence-modal-close"
            aria-label="Close"
            onClick={closeAndReset}
          >
            &times;
          </button>
        </div>

        <div className="evidence-modal-tabs" role="tablist" aria-label="Evidence tabs">
          {configureAndGetTabs().map((tab) => {
            const isActive = tab.id === activeOption;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`evidence-tab ${isActive ? "is-active" : ""}`}
                onClick={() => handleOptionSelect(tab.id)}
              >
                {tab.name}
              </button>
            );
          })}
        </div>

        <div className="evidence-modal-body">
          {activeOption === 1 ? (
            <div>
              {isFootballProduct ? (
                <div style={{ textAlign: "center", margin: "20px 0" }}>
                  <img
                    src={footballImage}
                    alt="Football"
                    style={{ height: 100, width: 100, margin: "0 auto" }}
                  />
                </div>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontWeight: "bold" }}>CONNECT FITNESS APP</span>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      margin: "10px 0",
                    }}
                  />

                  <div className="app-activity-container">
                    {appActivityData?.length ? (
                      <>
                        <div style={{ display: "flex", justifyContent: "center", margin: "16px 0" }}>
                          <button
                            type="button"
                            className="disconnect-strava-btn"
                            onClick={handleLogout}
                          >
                            Disconnect Strava
                          </button>
                        </div>

                        <div className="strava-activity-list">
                          {appActivityData.map((activity) => {
                            const isSubmitted = Boolean(activity.isSubmitted);
                            const isSelected = Boolean(activity.isSelected);

                            return (
                              <button
                                key={activity.id}
                                type="button"
                                className={`strava-pill ${isSelected ? "is-selected" : ""} ${
                                  isSubmitted ? "is-submitted" : ""
                                }`}
                                onClick={() => !isSubmitted && handleActivitySelect(activity)}
                                disabled={isSubmitted}
                              >
                                <div className="strava-pill-top">
                                  <span className="strava-pill-title">{activity.name}</span>
                                  {isSubmitted && (
                                    <span className="strava-pill-badge">Submitted</span>
                                  )}
                                </div>

                                <div className="strava-pill-meta">
                                  <span>{Number(activity.distance || 0).toFixed(2)} km</span>
                                  <span>
                                    {activity.duration_hms ||
                                      formatDuration(
                                        activity.duration ?? activity.elapsed_time ?? 0
                                      )}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {disEvedenceKm || durEvidenceSec ? (
                          <div className="strava-selection-summary">
                            <span>
                              Selected Distance: {Number(disEvedenceKm || 0).toFixed(2)} km
                            </span>
                            <span>
                              Selected Time: {formatDuration(durEvidenceSec || 0)}
                            </span>
                          </div>
                        ) : null}
                      </>
                    ) : (
                      appsToConnect.map((item) => (
                        <div
                          key={item.name}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            cursor: "pointer",
                            margin: 8,
                            padding: 5,
                            borderRadius: 10,
                            border: app === item.name ? "solid 5px #cb7c46" : "none",
                          }}
                          onClick={() =>
                            app === item.name ? null : handleClick(item.redirectUrl)
                          }
                        >
                          <img
                            style={{ height: 41, width: 201 }}
                            src={item.logo}
                            alt={item.name}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              <span style={{ fontWeight: "bold", paddingBottom: 5, marginBottom: 12 }}>
                {isFootballProduct ? "FOOTBALL CHALLENGE" : "MANUAL ENTRY"}
              </span>

              {!isFootballProduct ? (
                <div className="evidence-segment" role="group" aria-label="Submission type">
                  <button
                    type="button"
                    className={`evidence-segment-btn ${
                      submissionType === "distance" ? "is-active" : ""
                    }`}
                    onClick={() => setSubmissionType("distance")}
                  >
                    Distance
                  </button>
                  <button
                    type="button"
                    className={`evidence-segment-btn ${
                      submissionType === "time" ? "is-active" : ""
                    }`}
                    onClick={() => setSubmissionType("time")}
                  >
                    Time
                  </button>
                  <button
                    type="button"
                    className={`evidence-segment-btn ${
                      submissionType === "ocr" ? "is-active" : ""
                    }`}
                    onClick={() => setSubmissionType("ocr")}
                  >
                    Upload
                  </button>
                </div>
              ) : null}

              {isFootballProduct ? (
                footballEvidenceFormConfig.map((eve) => (
                  <div key={eve.key} style={{ marginBottom: 10, marginTop: 5 }}>
                    {eve.component}
                  </div>
                ))
              ) : submissionType === "time" ? (
                timeEvidenceFormConfig.map((eve) => (
                  <div key={eve.key} style={{ marginBottom: 10, marginTop: 5 }}>
                    {eve.component}
                  </div>
                ))
              ) : submissionType === "ocr" ? (
                <div style={{ marginBottom: 10, marginTop: 5 }}>
                  <OcrImageUploader
                    onParsed={async ({ distanceKm, durationSec }) => {
                      const payload = {
                        distanceKm,
                        durationSec: durationSec || 0,
                      };

                      const confirmResult = await Swal.fire({
  title: `PLEASE CHECK AND CONFIRM ${distanceKm.toFixed(2)} KM and time ${formatDuration(durationSec || 0)}?`,
  text: "Please make sure the distance is correct before submitting.",
  showCancelButton: true,
  confirmButtonText: "CONFIRM",
  cancelButtonText: "Cancel",
});

                      if (confirmResult.isConfirmed) {
                        try {
                          const resp = await submitEvidence(productId, userId, payload);
                          if (resp?.is_success) {
                            getUser();
                            Swal.fire(
                              "Evidence Saved",
                              `Distance: ${distanceKm.toFixed(
                                2
                              )} KM | Time: ${formatDuration(durationSec || 0)}`,
                              "success"
                            );
                          } else {
                            Swal.fire("Something went wrong", "Try again later", "error");
                          }
                        } catch (err) {
                          console.error(err);
                          Swal.fire("Something went wrong", "Try again later", "error");
                        }
                      }

                      setDisEvedenceKm(null);
                      setDurEvidenceSec(null);
                      setTimeMinutes(0);
                      clearSelection();
                      onClose();
                    }}
                    onError={(errMsg) => {
                      Swal.fire("OCR Error", errMsg, "error");
                    }}
                  />
                </div>
              ) : (
                evidenceFormConfig.map((eve) => (
                  <div key={eve.key} style={{ marginBottom: 10, marginTop: 5 }}>
                    {eve.component}
                  </div>
                ))
              )}

              {submissionType !== "ocr" && (
                <div className="evidence-modal-footer">
                  <button
                    disabled={isSubmitDisabled}
                    className="submit-btn evidence-submit"
                    onClick={onSubmitEvidence}
                  >
                    SUBMIT
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <EvidenceHistory onClose={onClose} getUser={getUser} status={status} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;

function DurationConverter({
  handleDurationChange,
  dur,
}: {
  handleDurationChange: (dur: number) => void;
  dur: number | null;
}) {
  const [hours, setHours] = useState<number>(0);
  const [minutes, setMinutes] = useState<number>(0);
  const [seconds, setSeconds] = useState<number>(0);

  useEffect(() => {
    const safeDur = Number(dur) || 0;
    const h = Math.floor(safeDur / 3600);
    const m = Math.floor((safeDur % 3600) / 60);
    const s = safeDur % 60;

    setHours(h);
    setMinutes(m);
    setSeconds(s);
  }, [dur]);

  const updateParent = (nextHours: number, nextMinutes: number, nextSeconds: number) => {
    const total = (nextHours || 0) * 3600 + (nextMinutes || 0) * 60 + (nextSeconds || 0);
    handleDurationChange(total);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <label>Duration (HH:MM:SS)</label>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "0.5rem",
          marginTop: "0.5rem",
        }}
      >
        <div>
          <input
            className="dur-inp"
            type="number"
            placeholder="HH"
            value={hours || ""}
            onChange={(e) => {
              const next = parseInt(e.target.value || "0", 10) || 0;
              setHours(next);
              updateParent(next, minutes, seconds);
            }}
          />
        </div>

        <div>
          <input
            className="dur-inp"
            type="number"
            placeholder="MM"
            value={minutes || ""}
            onChange={(e) => {
              const next = parseInt(e.target.value || "0", 10) || 0;
              setMinutes(next);
              updateParent(hours, next, seconds);
            }}
          />
        </div>

        <div>
          <input
            className="dur-inp"
            type="number"
            placeholder="SS"
            value={seconds || ""}
            onChange={(e) => {
              const next = parseInt(e.target.value || "0", 10) || 0;
              setSeconds(next);
              updateParent(hours, minutes, next);
            }}
          />
        </div>
      </div>
    </div>
  );
}

function DistanceConverter({
  handleDistanceChange,
  dis,
}: {
  handleDistanceChange: (dis: number) => void;
  dis: number | null;
}) {
  const [value, setValue] = useState<string>("");
  const [unit, setUnit] = useState<"km" | "miles" | "steps">("km");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (isEditing) return;

    if (dis == null || Number.isNaN(dis)) {
      setValue("");
      return;
    }

    if (unit === "km") setValue(String(dis));
    if (unit === "miles") setValue(String(Number(dis * 0.621371).toFixed(2)));
    if (unit === "steps") setValue(String(Math.round(Number(dis) * 1300)));
  }, [dis, unit, isEditing]);

  const convertToKm = (
    rawValue: string,
    selectedUnit: "km" | "miles" | "steps"
  ) => {
    const num = parseFloat(rawValue);
    if (!rawValue || Number.isNaN(num)) return 0;

    if (selectedUnit === "km") return num;
    if (selectedUnit === "miles") return num / 0.621371;
    if (selectedUnit === "steps") return num / 1300;

    return 0;
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    setValue(nextValue);

    if (nextValue === "") {
      handleDistanceChange(0);
      return;
    }

    const kmValue = convertToKm(nextValue, unit);
    handleDistanceChange(Number(kmValue.toFixed(2)));
  };

  const handleUnitChange = (nextUnit: "km" | "miles" | "steps") => {
    const kmValue = convertToKm(value, unit);
    setUnit(nextUnit);

    if (!value || Number.isNaN(kmValue)) {
      setValue("");
      return;
    }

    if (nextUnit === "km") setValue(String(Number(kmValue.toFixed(2))));
    if (nextUnit === "miles")
      setValue(String(Number((kmValue * 0.621371).toFixed(2))));
    if (nextUnit === "steps") setValue(String(Math.round(kmValue * 1300)));
  };

  return (
    <div className="distance-converter-modern">
      <label className="field-label">Choose Your Units</label>

      <div className="unit-pill-group">
        {(["km", "miles", "steps"] as const).map((item) => (
          <button
            key={item}
            type="button"
            className={`unit-pill ${unit === item ? "is-active" : ""}`}
            onClick={() => handleUnitChange(item)}
          >
            {item === "km" ? "KM" : item === "miles" ? "Miles" : "Steps"}
          </button>
        ))}
      </div>

      <input
        className="modern-distance-input"
        type="number"
        placeholder={
          unit === "km"
            ? "Enter distance in KM"
            : unit === "miles"
            ? "Enter distance in Miles"
            : "Enter steps"
        }
        value={value}
        onFocus={() => setIsEditing(true)}
        onBlur={() => setIsEditing(false)}
        onChange={handleValueChange}
      />
    </div>
  );
}

function TimeConverter({
  handleTimeChange,
  timeMinutes,
}: {
  handleTimeChange: (min: number) => void;
  timeMinutes: number;
}) {
  return (
    <div>
      <label>Time (minutes)</label>
      <input
        style={{
          marginTop: 10,
          padding: 10,
          borderRadius: 15,
          fontSize: 18,
          width: "90%",
        }}
        type="number"
        placeholder="Enter minutes"
        value={timeMinutes === 0 ? "" : timeMinutes}
        onChange={(e) => {
          const newValue = e.target.value;
          if (newValue === "") {
            handleTimeChange(0);
          } else {
            handleTimeChange(parseInt(newValue, 10) || 0);
          }
        }}
      />
    </div>
  );
}

function TimeRatioSelector({
  ratio,
  handleRatioChange,
}: {
  ratio: number;
  handleRatioChange: (value: number) => void;
}) {
  const activities = [
    { id: "dancing", label: "Dancing", ratio: 7.69, icon: "💃" },
    { id: "weight_lifting", label: "Weight Lifting", ratio: 10.5, icon: "🏋️" },
    { id: "football", label: "Sport", ratio: 9.09, icon: "⚽" },
    { id: "horse_riding", label: "Horse Riding", ratio: 10, icon: "🐎" },
    { id: "hiit", label: "HIIT", ratio: 6.0, icon: "🔥" },
    { id: "pilates", label: "Pilates", ratio: 8.0, icon: "🤸" },
    { id: "yoga", label: "Yoga", ratio: 11.0, icon: "🧘" },
    { id: "martial_arts", label: "Gardening", ratio: 12.5, icon: "🌻" },
  ];

  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (activity: { id: string; ratio: number }) => {
    setSelected(activity.id);
    handleRatioChange(activity.ratio);
  };

  return (
    <div>
      <label>Select Activity</label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "10px",
          marginTop: "8px",
        }}
      >
        {activities.map((activity) => (
          <div
            key={activity.id}
            onClick={() => handleSelect(activity)}
            style={{
              cursor: "pointer",
              padding: "8px",
              border:
                selected === activity.id ? "2px solid #c17846" : "1px solid #ccc",
              borderRadius: "12px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "24px" }}>{activity.icon}</div>
            <div style={{ fontSize: "12px" }}>{activity.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: "10px" }}>
        <label>Or enter custom minutes per KM:</label>
        <input
          type="number"
          value={ratio}
          onChange={(e) => {
            setSelected(null);
            handleRatioChange(parseFloat(e.target.value) || 0);
          }}
          style={{
            marginTop: "5px",
            padding: "8px",
            width: "90%",
            borderRadius: "15px",
            fontSize: "18px",
          }}
          placeholder="Enter conversion ratio"
        />
      </div>
    </div>
  );
}

function FootballSelector({
  handleFootballConversion,
}: {
  handleFootballConversion: (value: number) => void;
}) {
  const options = [
    { id: "garden", label: "Garden", ratio: 8.0, icon: "🌳" },
    { id: "park", label: "Park", ratio: 9.0, icon: "🏞" },
    { id: "training", label: "Training", ratio: 7.5, icon: "🏟️" },
    { id: "match", label: "Match", ratio: 6.5, icon: "⚽" },
  ];

  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (option: { id: string; ratio: number }) => {
    setSelected(option.id);
    handleFootballConversion(option.ratio);
  };

  return (
    <div>
      <label>Select Football Option:</label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "10px",
          marginTop: "8px",
        }}
      >
        {options.map((option) => (
          <div
            key={option.id}
            onClick={() => handleSelect(option)}
            style={{
              cursor: "pointer",
              padding: "8px",
              border: selected === option.id ? "2px solid #c17846" : "1px solid #ccc",
              borderRadius: "12px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "24px" }}>{option.icon}</div>
            <div style={{ fontSize: "12px" }}>{option.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EvidenceHistory({ onClose, getUser, status }) {
  const params = useParams();
  const { productId, userId } = params;
  const [evidences, setEvidences] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  function formatDate(timestamp: string | number | Date) {
    const date = new Date(timestamp);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const formattedDate = date.toLocaleDateString("en-US", options);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? "PM" : "AM";
    const formattedTime = `${hours % 12 || 12}:${minutes
      .toString()
      .padStart(2, "0")} ${period}`;
    return `${formattedDate}, ${formattedTime}`;
  }

  useEffect(() => {
    getEvidences();
  }, []);

  const getEvidences = async () => {
    try {
      const data = await getUserEvidences(productId, userId);
      setEvidences(Array.isArray(data) ? data : []);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    Swal.fire({
      title: "Do you want to delete?",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteUserEvidence(id);
          if (response?.is_success) {
            getUser();
            Swal.fire("Evidence deleted successfully", "", "success");
          } else {
            Swal.fire("Something went wrong", "Try again later", "error");
          }
          onClose();
        } catch (error) {
          console.log("err", error);
          Swal.fire("Something went wrong", "Try again later", "error");
          onClose();
        }
      }
    });
  };

  return (
    <>
      {!isLoading
        ? evidences.map((evidence) => (
            <div className="evidence" key={evidence._id}>
              <div>
                <span>{formatDate(evidence.created_on)}</span>
                <br />
                <span>Distance: {Number(evidence.distanceKm || 0).toFixed(2)} km</span>
                {evidence.durationSec ? (
                  <>
                    <br />
                    <span>
                      Duration: {evidence.duration_hms || formatDuration(evidence.durationSec)}
                    </span>
                  </>
                ) : null}
              </div>

              {!status ? (
                <button
                  onClick={() => handleDelete(evidence._id)}
                  style={{
                    height: 50,
                    padding: 8,
                    borderRadius: 100,
                    alignSelf: "center",
                    cursor: "pointer",
                  }}
                >
                  delete
                </button>
              ) : null}
            </div>
          ))
        : "loading..."}
    </>
  );
}

function ImageUploader() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const readFile = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(String(reader.result || ""));
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      readFile(file);
    } else {
      setSelectedImage(null);
      alert("Please select a valid image file.");
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith("image/")) {
        readFile(file);
      }
      e.dataTransfer.clearData();
    }
  };

  return (
    <div>
      <p style={{ marginBottom: 8, fontWeight: 500 }}>Add a photo:</p>

      <div
        className="image-upload-container"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        <div className="image-upload-label">
          <div className="upload-icon">📤</div>
          <span className="upload-text">Click or drag & drop to upload</span>
        </div>
      </div>

      {selectedImage && (
        <div className="image-preview-container">
          <img src={selectedImage} alt="Preview" className="image-preview" />
        </div>
      )}
    </div>
  );
}